import {
  AGENT_BLOCK_TYPES,
  AGENT_CONTEXT_LABEL_MAX_LENGTH,
  AGENT_KINDS,
  AGENT_MESSAGE_STATUSES,
  AGENT_PANEL_WIDTH,
  AGENT_STREAM_EVENTS,
  AGENT_SUGGESTED_PROMPTS,
  AGENT_TAB_LABELS
} from './_enums';

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
// Built from AGENT_KINDS, so a new kind needs no regex edit.
const REF_REGEX = new RegExp(`^zebel://(${Object.keys(AGENT_KINDS).join('|')})/(${UUID})$`);
const UUID_REGEX = new RegExp(`^${UUID}$`);

// ─── Refs ────────────────────────────────────────────────────────────

/** `{ kind, uid }` for a zebel://<kind>/<uid> link of a known kind, otherwise null. */
export const parseRef = (href) => {
  const match = typeof href === 'string' && href.match(REF_REGEX);
  return match ? { kind: match[1], uid: match[2].toLowerCase() } : null;
};

const toRef = (kind, uid) => `zebel://${kind}/${uid}`;

/** The key the API's ref summaries are stored under. */
export const refKey = ({ kind, uid }) => `${kind}/${uid}`;

/** Whether `tab` is one of the tabs a record of this kind has. */
export const isAgentTab = (kind, tab) => typeof tab === 'string' && Object.hasOwn(AGENT_TAB_LABELS, kind) && Object.hasOwn(AGENT_TAB_LABELS[kind], tab);

/**
 * The app path that opens a record, on `tab` when it is one of the kind's
 * tabs. Any other tab — model output, so anything at all — opens the record's
 * first tab rather than a path the app does not route.
 */
export const pathForRef = ({ kind, uid }, tab) => AGENT_KINDS[kind].path(uid, isAgentTab(kind, tab) ? tab : undefined);

// The schemes a link in a reply may use. Anything else (javascript:, data:,
// zebel:// of a kind we do not know) loses its href and renders as text.
const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/** Whether a link in the assistant's Markdown is one the panel will follow. */
export const isSafeLink = (href) => {
  if (typeof href !== 'string') return false;
  try {
    return SAFE_LINK_PROTOCOLS.includes(new URL(href).protocol);
  } catch {
    return false;
  }
};

/**
 * react-markdown's `urlTransform`: keeps a known record link and a safe link,
 * and blanks everything else. Images never reach it — they are disallowed.
 */
export const agentUrlTransform = (url) => (parseRef(url) || isSafeLink(url) ? url : '');

// ─── Streaming ───────────────────────────────────────────────────────

/**
 * Splits what has arrived of a newline-delimited JSON stream into the whole
 * events so far and the partial line still waiting for the rest of itself.
 * Pass the leftover `rest` back in, with the next chunk appended.
 *
 * A line that is not JSON throws rather than being skipped: the stream comes
 * from our own API, so an unreadable line is a defect to hear about, not data
 * to drop quietly.
 */
export const splitNdjson = (buffer) => {
  const lines = buffer.split('\n');
  const rest = lines.pop();
  const events = lines
    .filter((line) => line.trim())
    .map((line) => {
      try {
        return JSON.parse(line);
      } catch {
        throw Error(`Could not read a line of the stream: ${line.slice(0, 80)}`);
      }
    });
  return { events, rest };
};

export const emptyTurn = (text) => ({
  text,
  userMessage: null,
  blocks: [],
  refs: {},
  status: AGENT_MESSAGE_STATUSES.RUNNING,
  assistantMessage: null,
  error: ''
});

/**
 * The live turn after one streamed event. Pure — it runs inside a state
 * updater, which StrictMode calls twice — and it builds text the way the API
 * does: text runs on in the last text block until a tool's block comes
 * between, so the live reply and the saved one match.
 */
export const applyStreamEvent = (turn, event) => {
  switch (event.type) {
    case AGENT_STREAM_EVENTS.MESSAGE:
      return { ...turn, userMessage: event.message };
    case AGENT_STREAM_EVENTS.TEXT: {
      const last = turn.blocks[turn.blocks.length - 1];
      if (last?.type === AGENT_BLOCK_TYPES.TEXT) {
        return { ...turn, blocks: [...turn.blocks.slice(0, -1), { ...last, text: last.text + event.delta }] };
      }
      return { ...turn, blocks: [...turn.blocks, { type: AGENT_BLOCK_TYPES.TEXT, text: event.delta }] };
    }
    case AGENT_STREAM_EVENTS.BLOCK:
      return { ...turn, blocks: [...turn.blocks, event.block] };
    case AGENT_STREAM_EVENTS.REFS:
      return { ...turn, refs: { ...turn.refs, ...event.refs } };
    case AGENT_STREAM_EVENTS.DONE:
      return { ...turn, status: event.message.status, assistantMessage: event.message };
    case AGENT_STREAM_EVENTS.ERROR:
      return { ...turn, error: event.message };
    default:
      return turn;
  }
};

// ─── Blocks ──────────────────────────────────────────────────────────

// Shown after everything else in a reply, in the order they came: the prompt
// tells the model these appear below its text, and an ask card is where the
// reply ends.
const TRAILING_BLOCKS = [AGENT_BLOCK_TYPES.SHOW, AGENT_BLOCK_TYPES.ASK];

/**
 * A reply's blocks in the order the chat shows them: text, tool steps and
 * navigation as they streamed, then what the assistant showed and asked —
 * whether it called those tools before or after writing.
 */
export const orderBlocks = (blocks) => [...blocks.filter((block) => !TRAILING_BLOCKS.includes(block.type)), ...blocks.filter((block) => TRAILING_BLOCKS.includes(block.type))];

/**
 * The blocks to render right now. While a reply is still being written, what
 * it showed and asked is held back: the model calls show before it writes, so
 * a table would appear first and then be pushed down by text arriving above
 * it. Held, they appear below the finished text instead, and nothing already
 * on screen moves. Returns how many are held, for the status line.
 */
export const blocksToRender = (blocks, isRunning) => {
  const ordered = orderBlocks(blocks);
  if (!isRunning) return { visible: ordered, heldCount: 0 };
  const visible = ordered.filter((block) => !TRAILING_BLOCKS.includes(block.type));
  return { visible, heldCount: ordered.length - visible.length };
};

/**
 * A bars block's data as the chart takes it: label and value only. recharts
 * spreads each data point's fields onto the shapes it draws, and a bar's
 * record link is called `ref` — a prop React reserves, which throws on a
 * string. Values that are not numbers are dropped rather than drawn wrong.
 */
export const toBarChartData = (bars = []) => bars.map((bar) => ({ label: String(bar.label ?? ''), value: Number(bar.value) })).filter((bar) => Number.isFinite(bar.value));

// Where the live turn begins among the saved messages, or -1 if it is not
// saved yet. By uid once the stream has named the user's message; before that,
// the last user message, if it says what the turn says. A turn can finish on
// the server before the panel reads its first event, so a refetch may already
// hold the question and its reply while the uid is still unknown.
const turnStartIn = (messages, turn) => {
  if (turn.userMessage) return messages.findIndex((message) => message.uid === turn.userMessage.uid);
  const lastUserIndex = messages.reduce((found, message, index) => (message.role === 'user' ? index : found), -1);
  return lastUserIndex !== -1 && messages[lastUserIndex].blocks[0]?.text === turn.text ? lastUserIndex : -1;
};

/**
 * The saved messages to show beside a live turn. The server saves the turn's
 * question before the reply and the reply before the stream ends, so a refetch
 * mid-turn can hold either; the turn shows both itself, so the saved copies —
 * its question and everything after it — are dropped.
 */
export const withoutTurnEcho = (messages, turn) => {
  if (!turn) return messages;
  const start = turnStartIn(messages, turn);
  return start === -1 ? messages : messages.slice(0, start);
};

// ─── Screen context ──────────────────────────────────────────────────

// Where each kind of record page lives: the path segments before the record's
// uid. Everything after the uid is the tab, then the tab's own section.
const RECORD_ROUTES = [
  { kind: 'estimate', prefix: ['estimate'] },
  { kind: 'record', prefix: ['project-record'] },
  { kind: 'template', prefix: ['settings', 'record-template'] }
];

const matchRecordRoute = (segments) =>
  RECORD_ROUTES.map((route) => ({ ...route, uid: segments[route.prefix.length] }))
    .filter((route) => route.prefix.every((segment, index) => segments[index] === segment))
    .find((route) => route.uid && UUID_REGEX.test(route.uid));

const notUid = (segment) => (segment && !UUID_REGEX.test(segment) ? segment : undefined);

/**
 * What every page tells the assistant for free, read off the URL: the kind of
 * page, the record open on it, its tab, and the tab's sub-section (Settings ›
 * General). A uid deeper down (one bid package, one VE item) is not a section.
 * A page adds its own state through `useAgentContext`.
 */
export const contextFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const route = matchRecordRoute(segments);
  if (route) {
    const [tab, section] = segments.slice(route.prefix.length + 1);
    return { path: pathname, view: route.kind, tab, section: notUid(section), refs: [toRef(route.kind, route.uid.toLowerCase())] };
  }
  return { path: pathname, view: segments[0] || 'home', tab: notUid(segments[1]), section: undefined, refs: [] };
};

/**
 * The context sent with a message: the route's, plus what the page added.
 * `label` names the page; anything else the page added goes in `extra`.
 */
export const buildAgentContext = (pathname, pageContext) => {
  const { label, ...extra } = pageContext;
  return {
    ...contextFromPath(pathname),
    ...(label && { label: String(label).slice(0, AGENT_CONTEXT_LABEL_MAX_LENGTH) }),
    ...(Object.keys(extra).length && { extra })
  };
};

const humanize = (segment) => segment.replace(/-/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase());

/** A tab as its own tab bar labels it, e.g. "Bid Packages" for estimate/bid-package. */
export const tabLabelFor = (view, tab) => {
  if (!tab) return null;
  return isAgentTab(view, tab) ? AGENT_TAB_LABELS[view][tab] : humanize(tab);
};

/**
 * What the "On: …" chip says the next message is about: the record, then the
 * tab and section it is open on — "Riverside - ALL / V0 · Bid Packages". Null
 * on a page with no record open.
 */
export const describeContext = (context, refs) => {
  const ref = context.refs?.[0] && parseRef(context.refs[0]);
  if (!ref) return null;
  const name = context.label || refs[refKey(ref)]?.title || AGENT_KINDS[ref.kind].label;
  const where = [tabLabelFor(context.view, context.tab), context.section && humanize(context.section)].filter(Boolean).join(' › ');
  return where ? `${name} · ${where}` : name;
};

/** What a fresh conversation offers to ask: for this tab of this page, else this page, else anything. */
export const suggestedPromptsFor = (view, tab) => {
  const key = [`${view}/${tab}`, view].find((candidate) => Object.hasOwn(AGENT_SUGGESTED_PROMPTS, candidate));
  return AGENT_SUGGESTED_PROMPTS[key ?? 'default'];
};

// ─── Panel ───────────────────────────────────────────────────────────

/** A panel width the layout can hold, from a drag or from storage. */
export const clampPanelWidth = (width) => {
  const value = Number(width);
  if (!Number.isFinite(value)) return AGENT_PANEL_WIDTH.DEFAULT;
  return Math.round(Math.min(AGENT_PANEL_WIDTH.MAX, Math.max(AGENT_PANEL_WIDTH.MIN, value)));
};
